import React, { FC, useEffect } from 'react';
import { ApolloProvider } from '@apollo/client';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';

import { SystemShell } from './SystemShell';
import { useSystemConnection } from './ConnectionManager';
import { Log, LogCategory } from './services/logger';
import { isAndroid } from 'react-device-detect';

interface ConnectedApolloProviderProps {
  children: React.ReactNode;
}

export const ConnectedApolloProvider: FC<ConnectedApolloProviderProps> = ({
  children,
}) => {
  const { client, connected, loading, refreshConnection } =
    useSystemConnection();
  const refreshConnectionRef = React.useRef(refreshConnection);

  Log.silly('rendering apollo provider', null, LogCategory.RENDERING);

  useEffect(() => {
    refreshConnectionRef.current = refreshConnection;
  }, [refreshConnection]);

  // On mobile devices, refresh the Apollo connection whenever the app returns to the foreground. This resets the
  // websocket client connection and resumes graphql subscriptions without remounting the app subtree.
  const isMobile = Capacitor.isNativePlatform();
  useEffect(() => {
    let listener: PluginListenerHandle | undefined;
    async function addResumeHandler() {
      listener = await App.addListener('resume', () => {
        if (
          !isAndroid ||
          (isAndroid &&
            !window.location.pathname.endsWith('/passkeys') &&
            window.location.pathname !== '/login')
        ) {
          refreshConnectionRef.current();
        }
      });
    }

    if (isMobile) {
      addResumeHandler().then(() => {
        // intentionally do nothing ... satisfies typescript checking.
      });
    }

    return () => {
      listener?.remove();
    };
  }, [isMobile]);

  return client ? (
    <ApolloProvider client={client}>
      {!loading && connected ? children : <SystemShell />}
    </ApolloProvider>
  ) : (
    <div>System loading ...</div>
  );
};
